import { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from 'expo-router';

import { IRestaurant } from '@rbi-ctg/store';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useOrderContext } from 'state/order';
import { ISelectOrderStore } from 'state/order/types';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { TRANSITION_ANIMATION_TIME_MS } from 'utils/constants';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

import { useDefaultServiceMode } from './use-default-service-mode';

export function useSelectStore() {
  const {
    selectOrderStore,
    reorder: { pendingReorder, setShouldHandleReorder },
  } = useOrderContext();
  const { selectServiceMode } = useServiceModeContext();
  const router = useRouter();

  const getDefaultServiceMode = useDefaultServiceMode();
  const { redirectTo } = useLocalSearchParams<{ redirectTo: keyof typeof routes }>();
  const navState = useNavigation().getState();
  const isStoreInfoModal = navState?.index > 0;
  const { prevPathname } = useUIContext();

  const dismissStoreLocator = useCallback(() => {
    // Always dismiss the current screen which is the store locator.
    if (router.canDismiss()) {
      router.dismiss();
    }
  }, [router]);

  const handleNavigationLogic = useCallback(() => {
    dismissStoreLocator();

    // If there's a specific redirect target, prioritize it
    if (redirectTo) {
      // For checkout, ensure cart is in navigation stack first
      if (redirectTo === routes.cartCheckout) {
        router.navigate(routes.cart);
        setTimeout(() => {
          router.navigate(redirectTo);
        }, TRANSITION_ANIMATION_TIME_MS);
      } else {
        router.navigate(redirectTo);
      }
    } else if (prevPathname === routes.base) {
      // If coming from home go to menu directly
      router.navigate(routes.menu);
    } else if (isStoreInfoModal) {
      router.back();
    } else {
      // Fallback: navigate to menu if no other conditions match
      router.navigate(routes.menu);
    }
  }, [dismissStoreLocator, router, redirectTo, prevPathname, isStoreInfoModal]);

  const selectStoreCallback = useCallback(
    async ({
      newStore,
      serviceMode,
      skipRedirection = false,
    }: {
      newStore: IRestaurant;
      serviceMode: ServiceMode;
      skipRedirection?: boolean;
    }) => {
      // TODO: We probably want to use dynamic event name here
      logEvent(CustomEventNames.ORDER_HERE_STORE_CONFIRMATION_FLOW, EventTypes.Navigation, {
        'Restaurant Id': newStore._id,
        'Restaurant Name': newStore.name,
      });
      // set service mode to ensure we aren't taken back to static menu
      // need to do this after previous checks so that we don't set service mode
      // when enableOrdering is false
      selectServiceMode(serviceMode);

      // if user has a pending reorder return and let the useEffect handle it below
      if (pendingReorder) {
        setShouldHandleReorder(true);
        return;
      }

      if (!skipRedirection) {
        handleNavigationLogic();
      }
      LocalStorage.setItem(StorageKeys.SKIP_AUTO_SELECT_PROMPT, true);
    },
    [pendingReorder, handleNavigationLogic, selectServiceMode, setShouldHandleReorder]
  );

  return useCallback(
    async ({
      newStore,
      requestedServiceMode,
      skipRedirection = false,
      skipChangeStoreMessage,
      callback,
    }: ISelectOrderStore) => {
      const defaultServiceMode = getDefaultServiceMode(newStore);
      const finalServiceMode = requestedServiceMode ?? defaultServiceMode;

      await selectOrderStore({
        newStore,
        requestedServiceMode: finalServiceMode,
        skipChangeStoreMessage,
        callback: () => {
          selectStoreCallback({
            newStore,
            serviceMode: finalServiceMode,
            skipRedirection,
          });
          callback?.();
        },
      });
    },
    [getDefaultServiceMode, selectOrderStore, selectStoreCallback]
  );
}
